import { Box, Flex, Paper, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { get } from 'lodash';

import { NumberInput, Tooltip } from '@components';
import { EVALUATION_CALCULATOR_PERCENTAGE_FACTOR } from '@constants';
import { useSettings } from '@hooks/useSettings';
import { toDecimal } from '@utils';
import { renderActiveMethodButton } from './CustomerViewEvaluationMethods';
import classes from './CustomerViewEvaluationMethods.module.css';
import { CustomerViewEvaluationMethodsFormValues } from './useCustomerViewEvaluationMethodsFormInitializer';

interface CustomerViewEvaluationCalculatorProps {
  form: UseFormReturnType<CustomerViewEvaluationMethodsFormValues>;
}

export const CustomerViewEvaluationCalculator: React.FC<CustomerViewEvaluationCalculatorProps> = ({ form }) => {
  const { evaluationMethods } = form.values;
  const { data: settings } = useSettings();

  const globalError = get(form.errors, 'evaluationMethods.evalMethodCalculator.globalError');

  return (
    <Paper
      className={`${classes.evaluationMethodContainer} ${
        globalError ? classes.hasError : evaluationMethods.evalMethodCalculator?.activateMethod || false ? classes.activeMethod : ''
      }`}
    >
      <Flex>
        <Box className={classes.evaluationMethodContainerHeaderBody}>
          <Text fz={14} mb={10}>
            {settings.isForwarder ? 'Deductive Method Calculator' : 'EvalMethod Calculator'}
          </Text>
        </Box>
        {renderActiveMethodButton(
          form,
          evaluationMethods.evalMethodCalculator?.activateMethod || false,
          'evaluationMethods.evalMethodCalculator.activateMethod',
        )}
      </Flex>

      <Box className={classes.evaluationMethodFormRow} pt={25}>
        <NumberInput
          {...form.getInputProps('evaluationMethods.evalMethodCalculator.marketplaceFees')}
          label="MarketplaceFees (%)"
          onChange={(value) => {
            form.setFieldValue('evaluationMethods.evalMethodCalculator.marketplaceFees', Number(value) / EVALUATION_CALCULATOR_PERCENTAGE_FACTOR);
          }}
          value={toDecimal(Number(evaluationMethods.evalMethodCalculator?.marketplaceFees) * EVALUATION_CALCULATOR_PERCENTAGE_FACTOR)}
        />
        <Tooltip content="MarketplaceFees (%) = Referral Fee + Fixed closing fee + Variable closing fee" />
      </Box>

      <Box className={classes.evaluationMethodFormRow}>
        <NumberInput
          {...form.getInputProps('evaluationMethods.evalMethodCalculator.fullfillmentCost')}
          label="Fullfillment cost (€/Unit)"
          onChange={(value) => {
            form.setFieldValue('evaluationMethods.evalMethodCalculator.fullfillmentCost', Number(value));
          }}
          value={Number(evaluationMethods.evalMethodCalculator?.fullfillmentCost)}
        />
        <Tooltip content="Fullfillment cost (€/Unit) = Last mile shipment costs to consumer + Packaging fee for shipment" />
      </Box>

      <Box className={classes.evaluationMethodFormRow}>
        <NumberInput
          {...form.getInputProps('evaluationMethods.evalMethodCalculator.storageCost')}
          label="Storage costs (€/Unit)"
          onChange={(value) => {
            form.setFieldValue('evaluationMethods.evalMethodCalculator.storageCost', Number(value));
          }}
          value={Number(evaluationMethods.evalMethodCalculator?.storageCost)}
        />
        <Tooltip content="Storage costs (€/Unit) = 0.06 * Average Inventory units stored / Estimated monthly units stored" />
      </Box>

      <Box className={classes.evaluationMethodFormRow}>
        <NumberInput
          {...form.getInputProps('evaluationMethods.evalMethodCalculator.otherCost')}
          label="Other costs ( % )"
          onChange={(value) => {
            form.setFieldValue('evaluationMethods.evalMethodCalculator.otherCost', Number(value) / EVALUATION_CALCULATOR_PERCENTAGE_FACTOR);
          }}
          value={toDecimal(Number(evaluationMethods.evalMethodCalculator?.otherCost) * EVALUATION_CALCULATOR_PERCENTAGE_FACTOR)}
        />
        <Tooltip content="Other costs ( % ) = Costs of goods sold + Miscellaneous costs" />
      </Box>

      <Box className={classes.evaluationMethodFormRow} pb={30}>
        <NumberInput
          {...form.getInputProps('evaluationMethods.evalMethodCalculator.priceDeviation')}
          label="Price deviation ( % )"
          onChange={(value) => {
            form.setFieldValue('evaluationMethods.evalMethodCalculator.priceDeviation', Number(value) / EVALUATION_CALCULATOR_PERCENTAGE_FACTOR);
          }}
          value={toDecimal(Number(evaluationMethods.evalMethodCalculator?.priceDeviation) * EVALUATION_CALCULATOR_PERCENTAGE_FACTOR)}
        />
        <Tooltip content="Price deviation ( % ) = Acceptable price deviation of x% compared to the reasonable value" />
      </Box>

      {!!globalError && <Box className={classes.error}>{globalError}</Box>}
    </Paper>
  );
};
